import { Modal, Spin } from 'antd';
import { Trans, useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';

interface SlotChangeConfirmModalProps {
  open: boolean;
  // The slot the merchant is moving TO — label + window for the body copy.
  slotLabel: string;
  slotWindow: string;
  isSaving: boolean;
  onApprove: () => void;
  onCancel: () => void;
}

// Confirmation gate shown when the merchant changes their preferred slot
// while they may have an open same-day pickup: applying the new slot MOVES
// that pickup (and all its orders) onto it, so the change is not a silent
// settings write. Approve applies; Cancel returns to the slot modal with
// nothing changed. Closable via mask/ESC — same as Cancel.
const SlotChangeConfirmModal = ({
  open,
  slotLabel,
  slotWindow,
  isSaving,
  onApprove,
  onCancel,
}: SlotChangeConfirmModalProps) => {
  const { t } = useTranslation();

  return (
    <Modal
      open={open}
      centered
      width={384}
      footer={null}
      closable={false}
      maskClosable={!isSaving}
      keyboard={!isSaving}
      onCancel={onCancel}
      transitionName=''
      maskTransitionName=''
    >
      <div className='py-2 text-center'>
        <span
          className='mx-auto flex h-14 w-14 items-center justify-center rounded-full'
          style={{ background: 'rgba(245,158,11,0.12)' }}
        >
          <AlertTriangle size={30} className='text-amber-500' />
        </span>
        <h2 className='mt-4 text-lg font-bold'>{String(t('slot_change_confirm_title'))}</h2>
        <p className='mt-1.5 text-sm text-[rgb(115,123,140)]'>
          {/* RTL bidi isolation (v2-task-8 §0.3): the window range renders
              inside a <bdi> so Arabic digits don't get reordered. */}
          <Trans
            i18nKey='slot_change_confirm_body'
            values={{ label: slotLabel, window: slotWindow }}
            components={{ strong: <strong className='text-[rgb(57,57,60)]' />, bdi: <bdi /> }}
          />
        </p>
        <div className='mt-5 flex items-center gap-3'>
          <button
            type='button'
            onClick={onCancel}
            disabled={isSaving}
            className='h-10 flex-1 rounded-lg border border-solid text-sm font-semibold transition-colors hover:bg-[rgb(245,245,243)] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-45 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[rgb(237,224,74)]'
            style={{ borderColor: 'rgb(232,230,227)' }}
          >
            {String(t('cancel'))}
          </button>
          <button
            type='button'
            onClick={onApprove}
            disabled={isSaving}
            className='inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-lg bg-[rgb(237,224,74)] text-sm font-semibold text-[rgb(57,57,60)] transition-colors hover:bg-[rgb(230,217,60)] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-45 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[rgb(237,224,74)]'
          >
            {isSaving ? <Spin size='small' /> : null}
            {String(t('slot_change_confirm_approve'))}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default SlotChangeConfirmModal;
